import { Component, ViewChild, Input, ElementRef, OnInit, OnChanges, Renderer2, EventEmitter, Output, HostListener, AfterViewInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, AbstractControl, AbstractControlDirective, ControlValueAccessor, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ILabel } from '../ILabel.model';
import { ILookup } from '../common/interfaces/ILookup.interface';
import { KeyboardCode } from '../common/enums/keyboard-code';
import { BehaviorSubject } from 'rxjs';
import { CpqSelectService } from './cpq-select.service';
import { addIfNotExists, any } from '../common/functions/js-linq';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CpqStyle } from '../common/models/CpqStyles.model';
import { CpqSelectRequest, CreateNewItemOptions } from './cpq-select.model';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
  selector: `cpq-select`,
  templateUrl: './cpq-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CpqSelectComponent,
      multi: true
    },
    CpqSelectService
  ],
  styleUrls: ['cpq-select.component.scss'],
  standalone: false
})
export class CpqSelectComponent<TKey extends string | number = string> implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit {
  @ViewChild('field') field: ElementRef;
  @ViewChild('suggestion') suggestion: ElementRef;
  @ViewChild('toltipDiv') tooltip: ElementRef;
  @ViewChild('cpqSelectInputField') cpqSelectInputField: ElementRef;
  @ViewChild(CdkOverlayOrigin) suggestionOverlayOrigin: CdkOverlayOrigin;

  private innerValue: TKey | null;

  // variable which indicate whenever value(ngModel) is changed
  private valueObserver = new BehaviorSubject<TKey | null>(null);
  @Input('items') items = new Array<ILookup<TKey, string>>();
  @Input('dropdownListClass') dropdownListClass: string = '';
  @Input('customInfoStyle') customInfoStyle: CpqStyle;
  @Input('label') label?: ILabel;
  @Input('type') type?: SelectType = SelectType.Simple;
  @Input('control') control?: AbstractControl | AbstractControlDirective;
  @Input('url') url?: string;
  @Input('query') query?: string;
  @Input('request') request?: CpqSelectRequest; // If lookups need to be recieved with other request method rather then GET
  @Input('showEmptySelection') showEmptySelection?: boolean; // Adds empty selection on top
  @Input('tabindex') tabindex?: number;
  @Input('setFirstOptionSelected') setFirstOptionSelected = true; // if it is true, selects first from items. It is used only in dom select
  @Input('infoMessage') infoMessage?: ILabel;
  @Input('isDisabled') isDisabled: boolean = false;
  @Input('iconType') iconType: IconType = IconType.Close;
  @Input('createNewItemOptions') createNewItemOptions: CreateNewItemOptions = { visible: false, callback: null };
  @Input('focusOnLoad') focusOnLoad: boolean = false;
  @Input('name') name: string = '';
  @Input('id') id: string = this.name;
  @Output('selectedItemText') selectedItemText = new EventEmitter<string>();
  @Output('onInfoClick') onInfoClick = new EventEmitter<any>();
  @Output('onChange') onChange: EventEmitter<TKey> = new EventEmitter();
  @Output('focusout') focusout: EventEmitter<FocusEvent> = new EventEmitter();

  public filteredList: Array<ILookup<TKey, string>>;
  public SelectType = SelectType;
  public searchControl = new FormControl<string>('');
  public noSearchResults = false;
  public IconType = IconType;
  public showTooltip: boolean;
  private dropdownMaxHeight: number = 200;

  // variable for item selection while navigate with arrow keys
  public navigationSelectedItemIndex = null;

  public cdkOverlayWidth: number;
  public cdkOverlayOpened: boolean;


  // ControlValueAccessor implementation
  private changed = (value: any): void => { };
  public touched = (_: any): void => { };

  get value(): any {
    return this.innerValue;
  }

  set value(value: any) {
    if (this.innerValue !== value) {
      this.innerValue = value;
      this.changed(value);
      this.valueObserver.next(value);

      const item = this.items?.find(element => element.id === value);
      if (item) {
        this.selectedItemText.emit(item.text);
      }
    }
  }

  @HostListener('document:click', ['$event']) onClick(event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.filteredList = [];
      this.noSearchResults = false;
      if (!this.innerValue) {
        this.query = null; // when user click outside the component and ngModel does not exist searchControl should be reset
      }

      this.showTooltip = !this.showTooltip;
      this.cdkOverlayWidth = 0;
      this.cdkOverlayOpened = false;
    }
  }

  writeValue(value: any) {
    this.innerValue = value;
    this.valueObserver.next(value);
  }

  registerOnChange(fn: (value: any) => void) {
    this.changed = fn;
    this.valueObserver.next(this.value);
  }

  registerOnTouched(fn: () => void) {
    this.touched = fn;
  }

  // end of ControlValueAccessor implementation

  constructor(
    private elementRef: ElementRef,
    public route: ActivatedRoute,
    private service: CpqSelectService,
    private renderer: Renderer2) {
  }

  ngOnChanges(changes): void {

    if (this.isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }

    // for Searchable type
    if (changes.items && changes.items.currentValue) {
      this.items = changes.items.currentValue;
      if (this.setFirstOptionSelected && this.type === SelectType.Simple) {
        if (!any(this.items, x => x.id === this.value) && (!this.value || this.value + '' !== '0') && this.items[0]) {
          if (this.items[0].id === null || this.items[0].id === undefined) {
            throw new Error('Items can not contain record with null value for id.');
          }
          setTimeout(() => {
            if (this.value === null || this.value === undefined) {
              this.value = this.items[0].id;
            }
          }, 0);
        }
      }
    }

    // for AsyncSearchable type
    if (changes.request && changes.request.currentValue) {
      this.items = new Array<ILookup<TKey, string>>();
      this.service.getLooukpsWithBody(this.request, '').subscribe((lookups: Array<ILookup<TKey, string>>) => {
        this.initItemsAsync(lookups);
      });
    } else if (changes.url && changes.url.currentValue) {
      this.items = new Array<ILookup<TKey, string>>();
      this.service.getLookups(this.url, '').subscribe(lookups => this.initItemsAsync(lookups as Array<ILookup<TKey, string>>));
    }
  }

  private initItemsAsync(lookups: Array<ILookup<TKey, string>>) {
    if (lookups) {
      this.items = lookups;
      if (this.showEmptySelection && this.items[0] && !this.items[0].text) {
        this.items.splice(0, 0, { id: null, text: '' });
      }
    }
    this.valueObserver.next(this.value);
  }

  ngOnInit(): void {
    this.filteredList = new Array<ILookup<TKey, string>>();
    if (this.type === SelectType.AsyncSearchable) {
      // initial items for async type
      if (this.request) {
        this.service.getLooukpsWithBody(this.request, '').subscribe((lookups: Array<ILookup<TKey, string>>) => this.items = lookups);
      } else {
        this.service.getLookups(this.url, '').subscribe((lookups: Array<ILookup<TKey, string>>) => this.items = lookups);
      }
    }
    this.valueObserver.subscribe(newValue => {
      // restarting a value(ngModel)
      if (newValue === null) {
        this.value = null;
        return;
      }
      if (newValue || (newValue + '' === '0')) {
        this.onChange.emit(newValue);
        if (this.type === SelectType.Searchable) {
          const itemText = this.findById(newValue);
          this.searchControl.setValue(itemText, { emitEvent: false });
        }
        if (this.type === SelectType.AsyncSearchable) {
          const itemText = this.findByIdAsync(newValue);
          this.searchControl.setValue(itemText, { emitEvent: false });
        }
      }
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(150), distinctUntilChanged())
      .subscribe(newValue => {
        if (newValue || newValue === '') {
          switch (this.type) {
            case SelectType.Searchable:
              this.filterItems(newValue);
              break;
            case SelectType.AsyncSearchable:
              this.filterItemsAsync(newValue);
              break;
          }
        }
      });
  }

  ngAfterViewInit() {
    // it doesn't work as desired in IE, so it's not supported on it
    if (this.focusOnLoad && !(!!window['MSInputMethodContext'] && !!document['documentMode'])) {
      this.cpqSelectInputField.nativeElement.focus();
    }
  }

  private filterItems(query) {
    this.noSearchResults = false;

    if (query === '') {
      this.filteredList = this.items.slice();
      this.value = null;
    }

    if (query) {
      this.filteredList = this.items.filter(el => {
        if (el.text.toLowerCase().indexOf(query.toLowerCase()) > -1) {
          return el;
        }
      });
    }

    if (this.filteredList.length === 0) {
      this.noResult();
    }
  }

  private filterItemsAsync(query) {
    if (!query) {
      this.value = null;
    }

    if (this.request) {
      this.service.getLooukpsWithBody(this.request, query).subscribe(lookups => this.processResponse(lookups));
    } else {
      this.service.getLookups(this.url, query).subscribe(lookups => this.processResponse(lookups));
    }
  }

  private processResponse(lookups) {
    if (lookups) {
      this.filteredList = lookups;
      if (lookups.length === 0) {
        this.noResult();
      }
    }
  }

  noResult() {
    this.value = null;
    this.noSearchResults = true;
  }

  /**
   * Event is passed so that dropdown containing select
   * wouldn't close due to event propagation
   * when selection is made
   */
  public select(item: ILookup<TKey, string>, event = null) {
    if (event) {
      event.stopPropagation();
    }
    if (item) {
      this.showTooltip = false;
      if (this.type === SelectType.AsyncSearchable) {
        addIfNotExists(this.items, item, x => x.id == item.id); // adds to items list only for async type bacuse items only contains inital 500 items. Items list is used for events emmiting, e.g. selectedItemText event
      }
      this.value = item.id;
      this.searchControl.setValue(item.text, { emitEvent: false });
      this.filteredList = [];
      this.noSearchResults = false;
    } else {
      // when user types not existing text in input field
    }

    this.onChange.emit(item?.id);
  }

  public deselect(focus = true) {
    this.value = null;
    this.query = null;
    if (focus) {
      setTimeout(() => this.field.nativeElement.focus(), 0);
    } else {
      setTimeout(() => document.getElementsByTagName('body')['0'].click(), 0);
    }
    this.onChange.emit(null);
  }

  public suggest() {
    if (this.items) {
      this.filteredList = this.items.slice();
    }
    this.navigationSelectedItemIndex = null;

    if ((this.field.nativeElement.getBoundingClientRect().bottom + this.dropdownMaxHeight) > window.innerHeight) {
      this.renderer.addClass(this.suggestion.nativeElement, 'up');
    } else {
      this.renderer.removeClass(this.suggestion.nativeElement, 'up');
    }

    this.cdkOverlayWidth = this.suggestionOverlayOrigin.elementRef.nativeElement.offsetWidth;
    this.cdkOverlayOpened = true;
  }

  public onFocusOut(event: FocusEvent) {
    this.focusout.emit(event);
  }

  public navigate(event: KeyboardEvent) {
    this.showTooltip = true;
    switch (event.key) {
      case KeyboardCode.ARROW_UP || KeyboardCode.ARROW_UP_IE:
        this.navigateUp();
        break;
      case KeyboardCode.ARROW_DOWN || KeyboardCode.ARROW_DOWN_IE:
        this.navigateDown();
        break;
      case KeyboardCode.ENTER || KeyboardCode.NUMPAD_ENTER:
        if (this.navigationSelectedItemIndex !== null && this.navigationSelectedItemIndex < this.filteredList.length) {
          this.select(this.filteredList[this.navigationSelectedItemIndex]);
        }
        break;
      default:
        break;
    }
  }

  private navigateUp() {
    if (this.navigationSelectedItemIndex === null || this.navigationSelectedItemIndex === 0) {
      this.navigationSelectedItemIndex = this.filteredList.length - 1;
    } else {
      this.navigationSelectedItemIndex--;
    }
  }

  private navigateDown() {
    if (this.navigationSelectedItemIndex === null || this.navigationSelectedItemIndex === this.filteredList.length - 1) {
      this.navigationSelectedItemIndex = 0;
    } else {
      this.navigationSelectedItemIndex++;
    }
  }

  private findById(id: any): string {
    if (this.items) {
      const item = this.items.find(el => el.id.toString() === id.toString());
      return item ? item.text : null;
    }

    return null;
  }

  private findByIdAsync(id: any): string {
    if (this.items) {
      const item = this.items.find(el => el.id.toString() === id.toString());
      if (item) {
        return item ? item.text : null;
      } else {
        id = typeof (+id) === 'number' ? +id : id;
        if (this.request) {
          this.service.getLooukpsWithBody(this.request, '', id ? id : null).subscribe((lookups: Array<ILookup<TKey, string>>) => this.processReponseById(id, lookups));
        } else {
          this.service.getLookups(this.url, '', id ? id : null).subscribe((lookups: Array<ILookup<TKey, string>>) => this.processReponseById(id, lookups));
        }
      }
    }
    return null;
  }

  private processReponseById(id: any, lookups: Array<ILookup<TKey, string>>) {
    if (lookups) {
      if (this.items && this.items.length) {
        lookups.forEach(lookup => {
          addIfNotExists(this.items, lookup, x => x.id === lookup.id);
        });
      } else {
        this.items = lookups;
      }
      const newlyAddedItem = this.items.find(x => x.id === id);
      if (id && newlyAddedItem) {
        this.valueObserver.next(id);
        // in case of selectedItemText set value is emitting event before the lookup has been
        // added to this.items, so here we emit again
        this.selectedItemText.emit(newlyAddedItem.text);
      } else {
        // if you have selected id but delete object in DB
      }
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.field) {
      if (this.isDisabled) {
        this.field.nativeElement.setAttribute('disabled', isDisabled);
      } else {
        this.field.nativeElement.removeAttribute("disabled");
      }
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === KeyboardCode.TAB) {
      this.filteredList = [];
      this.noSearchResults = false;
    }

    if (event.key === KeyboardCode.ENTER || event.key === KeyboardCode.NUMPAD_ENTER) {
      event.preventDefault();
      this.select(this.filteredList[this.navigationSelectedItemIndex]);
    }
  }

  infoClicked() {
    this.onInfoClick.emit();
  }

  createNew() {
    if (this.createNewItemOptions.callback) {
      this.createNewItemOptions.callback();
    } else {
      this.items.push({ id: '0' as TKey, text: this.query });
      this.select({ id: '0' as TKey, text: this.query });
    }
  }

  toggleActionMenu(): void {
    this.navigationSelectedItemIndex = this.filteredList.findIndex(x => x.id === this.value);
    this.showTooltip = !this.showTooltip;
  }
}

export enum SelectType {
  Simple = 1,
  Searchable,
  AsyncSearchable
}

export enum IconType {
  Arrow,
  Close
}
